import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';
import type { UserLimits } from '../../types/user';
import type { DisplayUser, EditUserFormData } from './types';

const LIMIT_LABELS: Record<keyof UserLimits, string> = {
  numTenders: 'Tenders',
  numDrafts: 'Draft Responses',
  numReviews: 'Reviews',
  numReports: 'Analysis Reports',
  numAdHocDocs: 'Ad-hoc Documents',
  numSectionChats: 'AI Messages',
};

interface UserResourceAllocationProps {
  user: DisplayUser;
  formData: EditUserFormData;
  canEditUser: boolean;
  getAvailability: (key: keyof UserLimits) => {
    max: number;
    usedByOthers: number;
    available: number;
  };
  onLimitChange: (key: keyof UserLimits, value: string) => void;
  onManageTeamAllocations: () => void;
}

export const UserResourceAllocation: React.FC<UserResourceAllocationProps> = ({
  user,
  formData,
  canEditUser,
  getAvailability,
  onLimitChange,
  onManageTeamAllocations,
}) => {
  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="h6">Resource Allocation</Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={onManageTeamAllocations}
          disabled={!formData.teamId}
        >
          Manage Team Allocations
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" paragraph>
        {user.status === 'pending'
          ? 'Set initial limits for this pending user. Once active, limits should be managed at the Team level.'
          : 'Limits are managed at the Team level.'}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {(Object.keys(LIMIT_LABELS) as Array<keyof UserLimits>).map((key) => {
          const { max, available } = getAvailability(key);
          const val = formData.limits[key] || 0;
          const isUnlimited = val === -1;
          const isMaxUnlimited = max === Infinity || max === -1;

          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1,
                bgcolor: 'background.default',
                borderRadius: 1,
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {LIMIT_LABELS[key]}
                </Typography>
                {user.status === 'pending' && canEditUser && !isMaxUnlimited && (
                  <Typography variant="caption" color="text.secondary">
                    Available to assign: {available}
                  </Typography>
                )}
              </Box>
              {user.status === 'pending' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    size="small"
                    type="number"
                    value={isUnlimited ? '' : val}
                    onChange={(e) => {
                      const newVal = parseInt(e.target.value, 10);
                      if (!Number.isNaN(newVal)) {
                        const clampedVal =
                          available === Infinity ? newVal : Math.min(newVal, available);
                        onLimitChange(key, Math.max(0, clampedVal).toString());
                      } else {
                        onLimitChange(key, '0');
                      }
                    }}
                    disabled={!canEditUser || isUnlimited}
                    sx={{ width: 100 }}
                    inputProps={{ min: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isUnlimited}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          onLimitChange(key, e.target.checked ? '-1' : '0');
                        }}
                        disabled={!canEditUser || (!isMaxUnlimited && max !== -1)}
                      />
                    }
                    label={<Typography variant="caption">Unlimited</Typography>}
                    sx={{ m: 0 }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" fontWeight="bold">
                  {isUnlimited ? '∞' : val} / {isMaxUnlimited ? '∞' : max}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};
